import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import moment from 'moment-timezone';

import {
  ENoPrior,
  IAutoQuote,
  IAutoQuoteApplication,
  IAutoCheckout,
  IDriver,
  IVehicle,
  IVehicleCoverageValues,
  ICoverageValues,
  TVehicleFormValues,
  IAutoDiscounts,
} from '../../interfaces';
import formatPrice from '../../services/formatPrice';
import updateValueAtPath from '../../services/updateValueAtPath';

import { getDriverId } from './drivers';
import getAdditionalInterest from './getAdditionalInterestObject';
import getLienholderObject from './getLienholderObject';
import getUpdatedVehicleVariables from './regionUtilities/getUpdatedVehicleVariables';
import { getQuoteStateFees } from './stateFees';

export type TReturnUpdateVehicleOnQuote =
  | {
      updatedQuote: IAutoQuote;
      updatedPrice: string | null;
      hasNoChanges?: boolean;
    }
  | {
      hasNoChanges: boolean;
      updatedQuote?: IAutoQuote;
      updatedPrice?: string;
    };

type TGetQuoteContainerConfigProps = {
  stateTheftFeeEligibilityCheck?: (v: IVehicle) => boolean;
};

type TStore = {
  quote: IAutoQuote;
  quoteCheckout: IAutoCheckout;
};

type TActions = {
  checkSessionTimeout?: () => Promise<void>;
  updateQuote: (
    quoteNumber: string,
    updatedQuote: IAutoQuote
  ) => Promise<IAutoQuote>;
  getQuoteCheckout: (
    quoteNumber: string,
    application: IAutoQuoteApplication
  ) => Promise<IAutoCheckout>;
  finalizeQuote: (
    quoteNumber: string,
    finalizeQuoteOpts: { username?: string; password?: string }
  ) => Promise<IAutoQuote>;
};

type TUpdateAndCheckoutQuote = {
  path: string;
  updatedValues: any;
};

function updateAndCheckoutQuote(
  { path, updatedValues }: TUpdateAndCheckoutQuote,
  quote: IAutoQuote,
  {
    checkSessionTimeout = () => Promise.resolve(),
    updateQuote,
    getQuoteCheckout,
  }: Omit<TActions, 'finalizeQuote'>
): Promise<{
  updatedQuote: IAutoQuote;
  updatedPrice: string | null;
  discounts: IAutoDiscounts[];
}> {
  const quoteNumber = quote?.quote_number;

  const updatedQuote = updateValueAtPath({ base: quote, path, updatedValues });
  return new Promise((resolve, reject) => {
    checkSessionTimeout()
      .then(() => updateQuote(quoteNumber, updatedQuote))
      .then((quote) => {
        return getQuoteCheckout(quoteNumber, quote?.application).then(
          (quoteCheckout) => {
            const monthlyBillingAmounts = find(
              get(quoteCheckout, 'billing_amounts'),
              {
                billing_cadence: 'monthly',
              }
            );
            const updatedPrice = monthlyBillingAmounts?.total
              ? Number(monthlyBillingAmounts.total).toFixed(2)
              : null;
            resolve({
              updatedQuote: quote,
              updatedPrice,
              discounts: quoteCheckout.discounts,
            });
          }
        );
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function updateInsuranceNoPriorStatus(
  status: ENoPrior,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
) {
  return updateAndCheckoutQuote(
    {
      path: `application.no_prior_reason`,
      updatedValues: status,
    },
    quote,
    actions
  );
}

export function getQuoteContainerProps(
  quote: IAutoQuote,
  quoteCheckout: {
    billing_amounts: any;
    discounts: any;
    has_existing_account: boolean;
  },
  configs?: TGetQuoteContainerConfigProps
) {
  const {
    stateTheftFee,
    stateFraudAssessmentFee,
    stateTheftFeePerVehicle,
    stateFraudAssessmentFeePerVehicle,
  } = getQuoteStateFees(
    quote,
    quoteCheckout,
    configs?.stateTheftFeeEligibilityCheck
  );

  const monthlyBillingAmounts = find(quoteCheckout?.billing_amounts, {
    billing_cadence: 'monthly',
  });

  const semiAnnualBillingAmounts = find(quoteCheckout?.billing_amounts, {
    billing_cadence: 'semi_annual',
  });

  const monthlyQuoteCost = monthlyBillingAmounts
    ? formatPrice(monthlyBillingAmounts.total)
    : '$0.00';

  const semiAnnualQuoteCost = semiAnnualBillingAmounts
    ? formatPrice(semiAnnualBillingAmounts.total)
    : '$0.00';

  const quoteCost = monthlyQuoteCost;

  const umFundFee = monthlyBillingAmounts
    ? monthlyBillingAmounts.line_items.find(
        ({ item_code }: { item_code: string }) => item_code === 'um_fund_fee'
      )
    : null;

  const fee = umFundFee || stateTheftFee || stateFraudAssessmentFee;

  const totalSemiAnnualCostWithFee = fee
    ? `$${(
        Number(semiAnnualBillingAmounts.total) +
        Number((umFundFee || stateTheftFee || stateFraudAssessmentFee).amount)
      ).toFixed(2)}`
    : `$${Number(semiAnnualBillingAmounts?.total || 0).toFixed(2)}`;

  const totalMonthlyCostWithFee = fee
    ? `$${(
        Number(monthlyBillingAmounts.total) +
        Number((umFundFee || stateTheftFee || stateFraudAssessmentFee).amount)
      ).toFixed(2)}`
    : `$${Number(monthlyBillingAmounts?.total || 0).toFixed(2)}`;

  const totalCostWithFee = totalMonthlyCostWithFee;

  return {
    monthlyBillingAmounts,
    monthlyQuoteCost,
    semiAnnualQuoteCost,
    quoteCost,
    totalCostWithFee,
    totalMonthlyCostWithFee,
    totalSemiAnnualCostWithFee,
    discounts: quoteCheckout?.discounts,
    hasExistingAccount: quoteCheckout?.has_existing_account || false,
    baseVehicleYear: Number(
      moment.utc(quote?.application?.policy_effective_date).format('YYYY')
    ),
    stateTheftFee,
    stateFraudAssessmentFee,
    stateTheftFeePerVehicle,
    stateFraudAssessmentFeePerVehicle,
    hasLineItem: !!fee,
  };
}

function addNewVehicleToQuote(values: TVehicleFormValues, quote: IAutoQuote) {
  const newVehicle = {
    ...omit(values, [
      'address_line1',
      'address_line2',
      'address_city',
      'address_region',
      'address_postal',
      'lienholder_name',
      'interested_party_name',
      'interested_party_address',
    ]),
    year: parseInt(values.year),
    make: values.make.toUpperCase(),
    model: values.model.toUpperCase(),
    style: values.style.toUpperCase(),
    garage_address: omit(quote?.application?.rating_address, [
      'latitude',
      'longitude',
    ]),
    variables: getUpdatedVehicleVariables({
      year: parseInt(values.year),
      ownership_status: values.ownership_status,
      garage_address: quote.application.rating_address,
      isNewVehicle: true,
      baseVehicleYear: Number(
        moment(quote.application.policy_effective_date).format('YYYY')
      ),
    }),
    lienholder: getLienholderObject(values),
    data_source: values.data_source || 'manual',
    ...((values.interested_party_name || values.interested_party_address) && {
      additional_interest: getAdditionalInterest(
        values.interested_party_name,
        values.interested_party_address
      ),
    }),
  };

  return {
    newVehicle,
  };
}

function buildUpdatedVehicle(values: TVehicleFormValues, quote: IAutoQuote) {
  const currentVehicleIndex = findIndex(quote.application.vehicles, {
    id: values.id,
  });
  const currentVehicle = quote?.application?.vehicles?.[currentVehicleIndex];
  const updatedVehicle = {
    ...omit(currentVehicle, ['additional_interest']),
    ...omit(values, [
      'address_line1',
      'address_line2',
      'address_city',
      'address_region',
      'address_postal',
      'lienholder_name',
      'interested_party_name',
      'interested_party_address',
    ]),
    year: parseInt(values.year),
    make: values.make.toUpperCase(),
    model: values.model.toUpperCase(),
    style: values.style.toUpperCase(),
    lienholder: getLienholderObject(values),
    variables: getUpdatedVehicleVariables({
      year: parseInt(values.year),
      ownership_status: values.ownership_status,
      variables: currentVehicle.variables,
      garage_address: currentVehicle.garage_address,
      baseVehicleYear: Number(
        moment(quote.application.policy_effective_date).format('YYYY')
      ),
    }),
    ...((values.interested_party_name || values.interested_party_address) && {
      additional_interest: getAdditionalInterest(
        values.interested_party_name,
        values.interested_party_address
      ),
    }),
  } as IVehicle;

  const hasNoChanges = isEqual(
    {
      vin: currentVehicle.vin || null,
      ...omit(currentVehicle, 'vin'),
    },
    updatedVehicle
  );

  return { hasNoChanges, currentVehicleIndex, updatedVehicle };
}

function addAdditionalHolder(driver: IDriver, quote: IAutoQuote) {
  const currentHolders = get(quote, 'application.holders');
  const holderInfo = {
    first_name: driver.first_name,
    last_name: driver.last_name,
    dob: driver.dob,
    address: quote.application.rating_address,
    position_on_policy: 2,
  };
  const updatedHolders = currentHolders.concat(holderInfo);
  return updatedHolders;
}

function removeAdditionalHolder(quote: IAutoQuote) {
  const pniHolder = find(get(quote, 'application.holders'), {
    position_on_policy: 1,
  });
  return [pniHolder];
}

export type TDriverFormValues = {
  age?: number | string | null;
  age_licensed?: number | null;
  dob: string | Date;
  reason_not_driving?:
    | 'was_never_licensed'
    | 'is_out_of_country'
    | 'has_learners_permit'
    | 'exclude_driver'
    | null;
  driving_license_number?: string | null;
  driving_license_state?: string | null;
  first_name: string;
  last_name: string;
  is_distant_student?: boolean;
  is_good_student?: boolean;
  relationship_to_insured: 'spouse' | 'self' | 'child' | 'other';
  role: 'driver' | 'non_driver' | 'pni';
  took_defensive_driving_course: boolean;
  defensive_driver_course_completion_date?: string | null;
  took_mature_driver_accident_course?: boolean;
  mature_driver_course_completion_date?: string | null;
  good_student_grade_date?: string | null;
  data_source?: 'manual' | 'adpf' | string;
  id?: string | number;
  year_licensed?: number | null;
};

function calculateYearLicensed({
  year_licensed,
  age_licensed,
  dob,
}: Partial<TDriverFormValues>) {
  if (year_licensed) {
    return year_licensed;
  }
  if (age_licensed) {
    return Number(moment.utc(dob).add(age_licensed, 'years').format('YYYY'));
  }
  return null;
}

function addNewDriverToQuote(
  values: TDriverFormValues,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
) {
  const isAdpf = values.data_source === 'adpf';
  const isDriver = values.role === 'driver' || values.role === 'pni';

  const nonDriverReasonValues = [
    'was_never_licensed',
    'is_out_of_country',
    'has_learners_permit',
  ].map((reason) => ({
    [reason]: isDriver ? false : values.reason_not_driving === reason,
  }));
  const updatedNonDriverReasons = Object.assign({}, ...nonDriverReasonValues);

  const omittedValues = omit(values, [
    'age_licensed',
    'reason_not_driving',
    'age',
  ]);
  const newDriver = {
    ...omittedValues,
    year_licensed: calculateYearLicensed(values),
    dob:
      values.data_source === 'adpf'
        ? values.dob
        : moment.utc(values.dob).format('YYYY-MM-DD'),
    data_source: values.data_source || 'manual',
    is_excluded: false,
    ...updatedNonDriverReasons,
  } as IDriver;

  if (newDriver.relationship_to_insured === 'spouse') {
    const updatedPolicyHolders = addAdditionalHolder(newDriver, quote);
    const updatedApplication = isAdpf
      ? {
          ...quote.application,
          holders: updatedPolicyHolders,
          drivers: [...quote.application.drivers, newDriver],
          metadata: {
            ...quote.application.metadata,
            suggested_drivers:
              quote.application.metadata.suggested_drivers.filter(
                (driver) => getDriverId(driver) !== getDriverId(newDriver)
              ),
          },
        }
      : {
          ...quote.application,
          holders: updatedPolicyHolders,
          drivers: [...quote.application.drivers, newDriver],
        };

    return updateAndCheckoutQuote(
      {
        path: `application`,
        updatedValues: updatedApplication,
      },
      quote,
      actions
    );
  }

  if (isAdpf) {
    const updatedApplication = {
      ...quote.application,
      drivers: [...quote.application.drivers, newDriver],
      metadata: {
        ...quote.application.metadata,
        suggested_drivers: quote.application.metadata.suggested_drivers.filter(
          (driver) => getDriverId(driver) !== getDriverId(newDriver)
        ),
      },
    };

    return updateAndCheckoutQuote(
      {
        path: `application`,
        updatedValues: updatedApplication,
      },
      quote,
      actions
    );
  }

  return updateAndCheckoutQuote(
    {
      path: `application.drivers[${quote.application.drivers.length}]`,
      updatedValues: newDriver,
    },
    quote,
    actions
  );
}

export type TUpdateDriverOnQuotePromise = Promise<
  | {
      updatedQuote: IAutoQuote;
      updatedPrice: string | null;
      hasNoChanges?: boolean;
    }
  | { hasNoChanges: boolean; updatedQuote?: IAutoQuote; updatedPrice?: string }
>;

function updateDriverOnQuote(
  values: TDriverFormValues,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
): TUpdateDriverOnQuotePromise {
  const isDriver = values.role === 'driver' || values.role === 'pni';

  const nonDriverReasonValues = [
    'was_never_licensed',
    'is_out_of_country',
    'has_learners_permit',
  ].map((reason) => ({
    [reason]: isDriver ? false : values.reason_not_driving === reason,
  }));
  const updatedNonDriverReasons = Object.assign({}, ...nonDriverReasonValues);

  const currentQuoteDrivers = quote?.application?.drivers || [];
  const currentDriverIndex = findIndex(currentQuoteDrivers, {
    id: values.id,
  });
  const currentDriver = currentQuoteDrivers[currentDriverIndex];
  const omittedValues = omit(values, [
    'age_licensed',
    'reason_not_driving',
    'age',
  ]);

  const updatedDriver = {
    ...currentDriver,
    ...omittedValues,
    year_licensed: calculateYearLicensed(values),
    dob:
      values.data_source === 'adpf'
        ? values.dob
        : moment.utc(values.dob).format('YYYY-MM-DD'),
    data_source: values.data_source || 'manual',
    is_excluded: false,
    ...updatedNonDriverReasons,
  } as IDriver;

  const hasNoChanges = isEqual(
    {
      driving_license_state: updatedDriver.driving_license_state || null,
      driving_license_number: updatedDriver.driving_license_number || null,
      year_licensed: updatedDriver.year_licensed || null,
      ...omit(updatedDriver, [
        'driving_license_state',
        'driving_license_number',
        'year_licensed',
      ]),
      dob: moment.utc(updatedDriver.dob).format('YYYY-MM-DD'),
    },
    {
      driving_license_state: currentDriver.driving_license_state || null,
      driving_license_number: currentDriver.driving_license_number || null,
      year_licensed: currentDriver.year_licensed || null,
      ...omit(currentDriver, [
        'driving_license_state',
        'driving_license_number',
        'year_licensed',
      ]),
      dob: moment.utc(currentDriver.dob).format('YYYY-MM-DD'),
    }
  );

  if (hasNoChanges) {
    return Promise.resolve({ hasNoChanges });
  }
  const isCurrentlySetAsSpouse =
    updatedDriver.relationship_to_insured === 'spouse';
  const wasPreviouslyASpouse = !!find(currentQuoteDrivers, {
    id: updatedDriver.id,
    relationship_to_insured: 'spouse',
  });
  const isAddSpouseHolder = !wasPreviouslyASpouse && isCurrentlySetAsSpouse;
  const isRemoveSpouseHolder = wasPreviouslyASpouse && !isCurrentlySetAsSpouse;

  if (isAddSpouseHolder || isRemoveSpouseHolder) {
    const updatedPolicyHolders = isAddSpouseHolder
      ? addAdditionalHolder(updatedDriver, quote)
      : removeAdditionalHolder(quote);

    const updatedDrivers = [...currentQuoteDrivers];
    updatedDrivers.splice(currentDriverIndex, 1, updatedDriver);

    const updatedApplication = {
      ...quote.application,
      holders: updatedPolicyHolders,
      drivers: updatedDrivers,
    };

    return updateAndCheckoutQuote(
      {
        path: `application`,
        updatedValues: updatedApplication,
      },
      quote,
      actions
    );
  }
  return updateAndCheckoutQuote(
    {
      path: `application.drivers[${currentDriverIndex}]`,
      updatedValues: updatedDriver,
    },
    quote,
    actions
  );
}

function removeVehicleFromQuote(
  vehicleId: string,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
) {
  const updatedVehicles = filter(
    quote?.application?.vehicles,
    (obj: IVehicle) => obj.id !== vehicleId
  );
  const updatedApplication = {
    ...quote.application,
    vehicles: updatedVehicles,
  };
  return updateAndCheckoutQuote(
    {
      path: `application`,
      updatedValues: updatedApplication,
    },
    quote,
    actions
  );
}

function removeDriverFromQuote(
  driver: IDriver,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
) {
  const updatedDrivers = filter(
    quote?.application?.drivers,
    (obj: IDriver) => obj.id !== driver.id
  );
  const updatedPolicyHolders =
    driver.relationship_to_insured === 'spouse'
      ? removeAdditionalHolder(quote)
      : quote?.application?.holders;

  const updatedApplication = {
    ...quote.application,
    holders: updatedPolicyHolders,
    drivers: updatedDrivers,
  };

  return updateAndCheckoutQuote(
    {
      path: `application`,
      updatedValues: updatedApplication,
    },
    quote,
    actions
  );
}

function addVehicleAndUpdateCoveragesOnQuote({
  coverageValues,
  vehicleValues,
  quote,
}: {
  coverageValues: ICoverageValues;
  vehicleValues: TVehicleFormValues;
  quote: IAutoQuote;
}) {
  const { updatedApplication } = updateCoveragesOnQuote(coverageValues, quote);
  const updatedQuote = updateValueAtPath({
    base: quote,
    path: 'application',
    updatedValues: updatedApplication,
  });

  const { newVehicle } = addNewVehicleToQuote(vehicleValues, updatedQuote);
  const updatedQuoteWithAddedVehicle = updateValueAtPath({
    base: updatedQuote,
    path: `application.vehicles[${quote.application.vehicles.length}]`,
    updatedValues: newVehicle,
  });

  return { updatedApplication: updatedQuoteWithAddedVehicle.application };
}

function updateVehicleAndCoveragesOnQuote({
  coverageValues,
  vehicleValues,
  quote,
}: {
  coverageValues: ICoverageValues;
  vehicleValues: TVehicleFormValues;
  quote: IAutoQuote;
}) {
  const { updatedApplication } = updateCoveragesOnQuote(coverageValues, quote);

  const updatedQuote = updateValueAtPath({
    base: quote,
    path: 'application',
    updatedValues: updatedApplication,
  });

  const { currentVehicleIndex, updatedVehicle, hasNoChanges } =
    buildUpdatedVehicle(vehicleValues, updatedQuote);

  const updatedQuoteWithVehicleChange = updateValueAtPath({
    base: updatedQuote,
    path: `application.vehicles[${currentVehicleIndex}]`,
    updatedValues: updatedVehicle,
  });

  return {
    hasNoChanges,
    updatedApplication: updatedQuoteWithVehicleChange.application,
  };
}

function updateCoveragesOnQuote(
  coverageValues: ICoverageValues,
  quote: IAutoQuote
) {
  const updatedVehicles = (quote?.application?.vehicles || []).map(
    (vehicle) => {
      const vehicleCoverage = find(coverageValues.vehicles, {
        id: vehicle.id,
      }) as IVehicleCoverageValues;
      if (vehicleCoverage) {
        return {
          ...vehicle,
          rideshare_use:
            !!vehicleCoverage.values.ridesharing ||
            !!vehicleCoverage.values.side_hustle,
          variables: vehicleCoverage.values,
        };
      }
      return vehicle;
    }
  );

  const updatedApplication = {
    ...quote?.application,
    vehicles: updatedVehicles,
    variables: omit(coverageValues, ['vehicles']),
  } as IAutoQuoteApplication;

  return { updatedApplication };
}

function updateCoverageStartDate(
  coverageStartDate: string,
  quote: IAutoQuote,
  actions: Omit<TActions, 'finalizeQuote'>
) {
  const updatedApplication: IAutoQuoteApplication = {
    ...quote?.application,
    policy_effective_date: coverageStartDate,
  };
  return updateAndCheckoutQuote(
    {
      path: `application`,
      updatedValues: updatedApplication,
    },
    quote,
    actions
  );
}

function submitQuoteCheckout(
  finalizedQuote: IAutoQuote,
  values: { email?: string; password?: string },
  quote: IAutoQuote,
  {
    checkSessionTimeout = () => new Promise((resolve) => resolve()),
    updateQuote,
    finalizeQuote,
  }: Omit<TActions, 'getQuoteCheckout'>
) {
  const { email, password } = values;

  const finalizeQuoteOpts =
    (!!password && !!email && { username: email, password }) || {};

  return checkSessionTimeout()
    .then(() => updateQuote(quote.quote_number, finalizedQuote))
    .then((updatedQuote) =>
      finalizeQuote(updatedQuote.quote_number, finalizeQuoteOpts)
    );
}

export function getQuoteContainerHelpers(
  {
    quote,
    quoteCheckout,
    configs,
  }: TStore & { configs?: TGetQuoteContainerConfigProps },
  {
    checkSessionTimeout,
    updateQuote,
    getQuoteCheckout,
    finalizeQuote,
  }: TActions
) {
  return {
    updateInsuranceNoPriorStatus: (value: ENoPrior) =>
      updateInsuranceNoPriorStatus(value, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    updateAndCheckoutQuote: ({
      path,
      updatedValues,
    }: TUpdateAndCheckoutQuote) =>
      updateAndCheckoutQuote({ path, updatedValues }, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    getQuoteContainerProps: () =>
      getQuoteContainerProps(quote, quoteCheckout, configs),
    addNewVehicleToQuote: (values: TVehicleFormValues) => {
      const { newVehicle } = addNewVehicleToQuote(values, quote);

      return updateAndCheckoutQuote(
        {
          path: `application.vehicles[${quote.application.vehicles.length}]`,
          updatedValues: newVehicle,
        },
        quote,
        {
          checkSessionTimeout,
          updateQuote,
          getQuoteCheckout,
        }
      );
    },
    updateVehicleOnQuote: (
      values: TVehicleFormValues
    ): Promise<TReturnUpdateVehicleOnQuote> => {
      const { hasNoChanges, currentVehicleIndex, updatedVehicle } =
        buildUpdatedVehicle(values, quote);

      if (hasNoChanges) {
        return Promise.resolve({ hasNoChanges });
      }
      return updateAndCheckoutQuote(
        {
          path: `application.vehicles[${currentVehicleIndex}]`,
          updatedValues: updatedVehicle,
        },
        quote,
        {
          checkSessionTimeout,
          updateQuote,
          getQuoteCheckout,
        }
      );
    },
    removeVehicleFromQuote: (vehicleId: string) =>
      removeVehicleFromQuote(vehicleId, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    addNewDriverToQuote: (values: TDriverFormValues) =>
      addNewDriverToQuote(values, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    updateDriverOnQuote: (values: TDriverFormValues) =>
      updateDriverOnQuote(values, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    removeDriverFromQuote: (driver: IDriver) =>
      removeDriverFromQuote(driver, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    updateCoveragesOnQuote: (values: ICoverageValues) => {
      const { updatedApplication } = updateCoveragesOnQuote(values, quote);

      return updateAndCheckoutQuote(
        {
          path: `application`,
          updatedValues: updatedApplication,
        },
        quote,
        {
          checkSessionTimeout,
          updateQuote,
          getQuoteCheckout,
        }
      );
    },
    updateVehicleAndCoveragesOnQuote: (
      coverageValues: ICoverageValues,
      vehicleValues: TVehicleFormValues
    ): Promise<TReturnUpdateVehicleOnQuote> => {
      const { updatedApplication, hasNoChanges } =
        updateVehicleAndCoveragesOnQuote({
          coverageValues,
          vehicleValues,
          quote,
        });

      if (hasNoChanges) {
        return Promise.resolve({ hasNoChanges });
      }

      return updateAndCheckoutQuote(
        {
          path: `application`,
          updatedValues: updatedApplication,
        },
        quote,
        {
          checkSessionTimeout,
          updateQuote,
          getQuoteCheckout,
        }
      );
    },
    addVehicleAndUpdateCoveragesOnQuote: (
      coverageValues: ICoverageValues,
      vehicleValues: TVehicleFormValues
    ) => {
      const { updatedApplication } = addVehicleAndUpdateCoveragesOnQuote({
        coverageValues,
        vehicleValues,
        quote,
      });
      return updateAndCheckoutQuote(
        {
          path: `application`,
          updatedValues: updatedApplication,
        },
        quote,
        {
          checkSessionTimeout,
          updateQuote,
          getQuoteCheckout,
        }
      );
    },
    updateCoverageStartDate: (coverageStartDate: string) =>
      updateCoverageStartDate(coverageStartDate, quote, {
        checkSessionTimeout,
        updateQuote,
        getQuoteCheckout,
      }),
    submitQuoteCheckout: (
      finalizedQuote: IAutoQuote,
      values: { email?: string; password?: string }
    ) =>
      submitQuoteCheckout(finalizedQuote, values, quote, {
        checkSessionTimeout,
        updateQuote,
        finalizeQuote,
      }),
  };
}
